import { inject, Injectable } from '@angular/core';
import { SqliteService } from '@app/services';
import { CheckInModel, CreateCheckInModel, RouteModel, StoreModel } from '@app/models';
import { UserRepository } from './user.repository';
import { formatDate } from '@app/helpers';
import * as Sentry from '@sentry/capacitor';

@Injectable({
    providedIn: 'root',
})
export class CheckInRepository
{
    private readonly keys = {
        tableName: 'check_ins',
    };
    private sqliteService = inject(SqliteService);
    private userRepository = inject(UserRepository);

    async clearUserRouteCheckIns(routeId: number): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const sql = `
            DELETE
            FROM ${this.keys.tableName}
            WHERE route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.delete(sql, [routeId, user.id]);
    }

    async updateCheckIn(checkIn: CheckInModel): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const columns = [
            'check_in',
            'check_out',
            'no_delivery_reason_id',
            'no_delivery_notes',
            'latitude',
            'longitude',
            'user_id',
        ];
        const setClause = columns.map((col) => `${col} = ?`).join(', ');
        const sql = `
            UPDATE ${this.keys.tableName}
            SET ${setClause}
            WHERE id = ?;
        `;
        await this.sqliteService.save(sql, [
            checkIn.check_in,
            checkIn.check_out,
            checkIn.no_delivery_reason_id,
            checkIn.no_delivery_notes,
            checkIn.latitude,
            checkIn.longitude,
            checkIn.user_id,
            checkIn.id,
        ]);
    }

    async createCheckIn(newCheckIn: CreateCheckInModel): Promise<CheckInModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        Sentry.addBreadcrumb({
            category: 'check-in',
            message: 'Checking existing check-in before create',
            data: {
                userId: user.id,
                routeId: newCheckIn.route_id,
                storeId: newCheckIn.store_id,
                checkIn: newCheckIn.check_in,
            },
            level: 'info',
        });

        const existingCheckIn = await this.getCheckIn(
            user.id,
            newCheckIn.store_id,
            newCheckIn.route_id,
            newCheckIn.check_in,
        );

        if (existingCheckIn)
        {
            Sentry.addBreadcrumb({
                category: 'check-in',
                message: 'Existing check-in found during create',
                data: {
                    existingCheckInId: existingCheckIn.id,
                    userId: existingCheckIn.user_id,
                    routeId: existingCheckIn.route_id,
                    storeId: existingCheckIn.store_id,
                    checkIn: existingCheckIn.check_in,
                    checkOut: existingCheckIn.check_out,
                },
                level: 'info',
            });
            return existingCheckIn;
        }

        const columns = [
            'user_id',
            'store_id',
            'route_id',
            'check_in',
            'check_out',
            'no_delivery_reason_id',
            'no_delivery_notes',
        ];
        const valuePlaceholders = this.sqliteService.getValuePlaceholder(1, columns.length);
        const sql = `
            INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
            VALUES ${valuePlaceholders};
        `;
        const id = await this.sqliteService.save(sql, [
            user.id,
            newCheckIn.store_id,
            newCheckIn.route_id,
            newCheckIn.check_in,
            newCheckIn.check_out,
            newCheckIn.no_delivery_reason_id,
            newCheckIn.no_delivery_notes,
        ]);

        if (id === null)
        {
            Sentry.addBreadcrumb({
                category: 'check-in',
                message: 'Check-in insert returned null id',
                data: {
                    userId: user.id,
                    routeId: newCheckIn.route_id,
                    storeId: newCheckIn.store_id,
                    checkIn: newCheckIn.check_in,
                },
                level: 'warning',
            });
            return null;
        }

        Sentry.addBreadcrumb({
            category: 'check-in',
            message: 'Inserted new check-in',
            data: {
                id,
                userId: user.id,
                routeId: newCheckIn.route_id,
                storeId: newCheckIn.store_id,
                checkIn: newCheckIn.check_in,
            },
            level: 'info',
        });

        return await this.getCheckInById(id);
    }

    async getCheckInById(id: number): Promise<CheckInModel | null>
    {
        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE id = ? LIMIT 1;
        `;
        const data = await this.sqliteService.get(sql, [id]);

        if (data === null)
        {
            return null;
        }

        return data[0] as CheckInModel;
    }

    async getTodayStoreCheckIn(store: StoreModel, route: RouteModel): Promise<CheckInModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        Sentry.addBreadcrumb({
            category: 'check-in',
            message: 'Loading today store check-in',
            data: {
                userId: user.id,
                routeId: route.id,
                storeId: store.id,
            },
            level: 'info',
        });

        return await this.getCheckIn(user.id, store.id, route.id, new Date().toISOString());
    }

    async getCheckIn(userId: number, storeId: number, routeId: number, date: string): Promise<CheckInModel | null>
    {
        let dateToQuery = date;

        if (dateToQuery !== 'now')
        {
            dateToQuery = formatDate(new Date(dateToQuery));
        }

        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE user_id = ?
              AND store_id = ?
              AND route_id = ?
              AND DATE (check_in) = DATE (?)
            ORDER BY id DESC
        `;
        const data = await this.sqliteService.get(sql, [userId, storeId, routeId, dateToQuery]);

        if (data === null)
        {
            Sentry.addBreadcrumb({
                category: 'check-in',
                message: 'No check-in found for lookup',
                data: {
                    userId,
                    routeId,
                    storeId,
                    dateToQuery,
                },
                level: 'info',
            });
            return null;
        }

        if (data.length > 1)
        {
            const latestCheckIn = data[0] as CheckInModel;

            Sentry.captureException(new Error('Duplicate same-day check-ins found'), {
                extra: {
                    userId,
                    routeId,
                    storeId,
                    dateToQuery,
                    duplicateCount: data.length,
                    latestCheckInId: latestCheckIn.id,
                    duplicateCheckInIds: data.map((item) => item.id),
                    duplicateCheckIns: data.map((item) => ({
                        id: item.id,
                        checkIn: item.check_in,
                        checkOut: item.check_out,
                        latitude: item.latitude,
                        longitude: item.longitude,
                    })),
                },
            });
        }

        const checkIn = data[0] as CheckInModel;

        Sentry.addBreadcrumb({
            category: 'check-in',
            message: 'Check-in found for lookup',
            data: {
                id: checkIn.id,
                userId: checkIn.user_id,
                routeId: checkIn.route_id,
                storeId: checkIn.store_id,
                checkIn: checkIn.check_in,
                checkOut: checkIn.check_out,
                dateToQuery,
            },
            level: 'info',
        });

        return checkIn;
    }
}
